import { useAuth } from "@clerk/clerk-react";
import { Box, Grid, Typography } from "@mui/material";
import { grey } from "@mui/material/colors";
import { AlertContext } from "contexts/Alert";
import { OrganizationUserContext } from "contexts/Organization";
import { UserIntegrationsContext } from "contexts/UserIntegrations";
import { useContext, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { VAStatus, VaOrganizationProfileStatus } from "schemas/dashboard";
import { routes } from "schemas/routes";

import ConnectGmailDialog from "components/ConnectGmailDialog";
import StyledCard from "components/Styled/Card";
import QuestionChip from "components/Styled/QuestionChip";
import StyledVaPage from "components/Styled/VAPage";
import { fetcherAuth } from "utils/api";
import { trackEvent } from "utils/tracking";
import { useTracking } from "utils/useTracking";

const styles = {
  questionBox: {
    border: `1px solid ${grey[300]}`,
    borderRadius: "16px",
    p: 2,
    width: "100%",
    mb: 2,
  },
};

const SelfServeVaReview = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { getToken } = useAuth();
  const { setAlert, setErrorAlert } = useContext(AlertContext);
  const { trackVaStep } = useTracking();

  const { currentOrg, setCurrentOrg, setOrganizationVaProfile } = useContext(
    OrganizationUserContext,
  );
  const { emailHealth, fetchIntegrationHealth, userIntegration, scopes } =
    useContext(UserIntegrationsContext);
  const { userIntegrations } = useContext(OrganizationUserContext);

  const [userIntegrationDialogState, setUserIntegrationDialogState] =
    useState(false);
  const [loading, setLoading] = useState(false);
  const [sendOption, setSendOption] = useState<"now" | "later" | null>(null);

  const connectToGmail = () => {
    trackEvent("VA Onboarding Flow - Connect to Gmail Button Clicked");
    setUserIntegrationDialogState(true);
  };

  const approveOrganizationProfile = async () => {
    if (currentOrg?.vaStatus === VAStatus.approved) {
      navigate(`/${routes.virtualAssistant}`);
      return;
    }

    if (!emailHealth) {
      connectToGmail();
      return;
    }

    if (!sendOption) {
      setAlert("Please select when to send your emails", "error");
      return;
    }

    setLoading(true);
    try {
      await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg?.id}/onboarding/approve`,
        "POST",
        {},
        {
          sendOption,
        },
      );
      setOrganizationVaProfile((prev) => {
        if (prev) {
          prev.status = VaOrganizationProfileStatus.completed;
        }
        return prev;
      });

      setCurrentOrg((prev) => {
        if (prev) {
          prev.vaStatus = VAStatus.approved;
        }
        return prev;
      });
      navigate(`/${routes.virtualAssistant}`);
    } catch (error) {
      if (error?.message?.includes("follow up question")) {
        setAlert("Please answer follow-up questions for your niches", "error");
        navigate(`/${routes.nichesFollowUpSelfServeOnboarding}`);
        return;
      }
      setErrorAlert(error);
      localStorage.removeItem(`${currentOrg?.id}-va-step`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (currentOrg?.id) {
      fetchIntegrationHealth(currentOrg.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentOrg?.id]);

  useEffect(() => {
    if (emailHealth && currentOrg?.id && sendOption) {
      approveOrganizationProfile();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [emailHealth, sendOption]);

  const onEmailConnected = async () => {
    setUserIntegrationDialogState(false);
  };

  return (
    <>
      <StyledVaPage
        title={"Complete Onboarding"}
        description={
          "Select when to schedule your email and click Connect to Gmail to allow Bento to send emails on your behalf."
        }
        body={
          <>
            <>
              <StyledCard styles={{ px: 2, py: 4, mb: 4 }}>
                <Box sx={styles.questionBox}>
                  <Typography variant="h6">
                    Would you like pitches to be sent immediately, or would you
                    prefer to schedule them later? *
                  </Typography>
                  <Typography></Typography>
                  <Typography variant="caption" color="textSecondary" paragraph>
                    If you choose “later,” you can go to your Virtual Assistant
                    dashboard and start them whenever you're ready.
                  </Typography>
                  <Grid container gap={1}>
                    <QuestionChip
                      onClick={() => setSendOption("now")}
                      isSelected={sendOption === "now"}
                      label="Immediately"
                    />
                    <QuestionChip
                      onClick={() => setSendOption("later")}
                      isSelected={sendOption === "later"}
                      label="Later"
                    />
                  </Grid>
                </Box>
              </StyledCard>
            </>
          </>
        }
        stepNumber={6}
        onBack={() => {
          navigate(`/${routes.emailWarmupSelfServeOnboarding}`);
          trackVaStep(routes.emailWarmupSelfServeOnboarding);
        }}
        buttonBack={"Back to Preferences"}
        buttonNext={"Connect To Gmail"}
        onNext={() => {
          if (!sendOption) {
            setAlert("Please select when to send your emails", "error");
            return;
          }
          if (!emailHealth) {
            connectToGmail();
          } else {
            approveOrganizationProfile();
            navigate(`/${routes.virtualAssistant}${location.search}`);
          }
        }}
        loading={loading}
      />
      <ConnectGmailDialog
        openIntegrationDialog={userIntegrationDialogState}
        setOpenIntegrationDialog={(newOpen: boolean) =>
          setUserIntegrationDialogState(newOpen)
        }
        userIntegrations={userIntegrations}
        userIntegration={userIntegration}
        scopes={scopes}
        emailHealth={emailHealth}
        onSuccessAction={onEmailConnected}
        title="Connect your Gmail account"
      />
    </>
  );
};

export default SelfServeVaReview;
